import { Controller } from "@hotwired/stimulus";
import { emailVisibleContentOffset } from "../includes/emailLayout";

// Waits out the browser's own initial scroll to whatever #anchor the page loaded with (animated
// by the global `scroll-behavior: smooth`, not JS) so flying past unread mail on the way there
// doesn't mark it read. Most loads land on #newest, which needs no real scroll now that the
// thread is newest-first -- but #unread (an old email re-matched onto the lead, still sitting at
// its original position) or a specific #email-match-<id> (e.g. after classifying an attachment)
// can each land deep in the thread and genuinely scroll past several cards. Cards that connect
// after the grace period (a reply, a lazy-loaded older page) land outside the viewport regardless,
// so they're observed immediately.
const GRACE_PERIOD_MS = 2000;

export default class extends Controller {
  static targets = ["email"];
  static values = { readable: Boolean };

  connect () {
    this.suppressed = sessionStorage.justMarkedUnread === "true";
    if (this.suppressed) sessionStorage.removeItem("justMarkedUnread");

    this.ready = false;
    this.pending = new Set();
    // suppressed only withholds cards present at this moment -- matching what the inline script
    // it replaced did (strip the target attribute from what's in the DOM at DOMContentLoaded).
    // Snapshotting membership rather than checking `suppressed` directly inside observeIfUnread
    // means a card lazy-loaded later in the same page view (an older page, a new reply) is never
    // affected by it, however Stimulus happens to order this callback relative to connect() for
    // cards already on the page -- it's content the admin is actively scrolling to just now, not
    // the email they intentionally left unread moments ago.
    this.initialTargets = new Set(this.emailTargets);

    if (!this.readableValue) return;

    this.intersectionObserver = new IntersectionObserver(this.handleIntersect, {
      rootMargin: `${-emailVisibleContentOffset()}px 0px 0px 0px`,
      threshold: 0,
    });

    if (!this.suppressed) {
      this.initialTargets.forEach(el => this.observeIfUnread(el));
    }

    this.graceTimeout = setTimeout(() => {
      this.ready = true;
      this.pending.forEach(el => this.intersectionObserver.observe(el));
      this.pending.clear();
    }, GRACE_PERIOD_MS);
  }

  disconnect () {
    clearTimeout(this.graceTimeout);
    this.intersectionObserver?.disconnect();
  }

  emailTargetConnected (el) {
    if (!this.intersectionObserver) return; // fires before connect() -- its own loop covers this element
    if (this.suppressed && this.initialTargets.has(el)) return;

    this.observeIfUnread(el);
  }

  emailTargetDisconnected (el) {
    this.pending.delete(el);
    this.intersectionObserver?.unobserve(el);
  }

  observeIfUnread (el) {
    if (el.dataset.emailRead !== "false") return;

    if (this.ready) {
      this.intersectionObserver.observe(el);
    } else {
      this.pending.add(el);
    }
  }

  handleIntersect = (entries) => {
    function markEmailAsRead (entry) {
      const form = entry.target.querySelector("form");
      Teamshares.Rails.fire(form, "submit");
    }
    const visibleEntries = entries.filter(entry => entry.isIntersecting);
    visibleEntries.forEach(markEmailAsRead);
    const unreadEmails = document.getElementById("unread-emails");
    if (unreadEmails) {
      const newNumUnread = parseInt(unreadEmails.innerText) - visibleEntries.length;
      if (newNumUnread <= 0) {
        unreadEmails.remove();
      } else {
        unreadEmails.innerText = `${newNumUnread}`;
      }
    }

    visibleEntries.forEach(entry => this.intersectionObserver.unobserve(entry.target));
  };
}
